import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
  getCurrentDevice,
  getVapidPublicKey,
  registerDevice,
  subscribeSelf,
  unsubscribe,
  type PushSubscriber,
} from "../api/push";
import { registerAppServiceWorker } from "../lib/serviceWorker";

// Поддръжка на Web Push в текущия браузър. На iPhone това е true само когато
// приложението е инсталирано на началния екран (iOS 16.4+) — в Safari таб
// PushManager липсва и UI-ят показва хинт вместо превключвателя.
export function isPushSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

const VAPID_KEY_QUERY_KEY = ["push", "vapid-public-key"] as const;
const DEVICE_QUERY_KEY = ["push", "device"] as const;

// pushManager.subscribe иска VAPID ключа като бинарен буфер, а BE го дава
// base64url — стандартната конверсия.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from(rawData, (char) => char.charCodeAt(0));
}

interface CurrentDeviceState {
  endpoint: string | null;
  subscribed: boolean;
  subscribers: PushSubscriber[];
}

async function readCurrentDevice(): Promise<CurrentDeviceState> {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) {
    return { endpoint: null, subscribed: false, subscribers: [] };
  }
  try {
    const device = await getCurrentDevice(subscription.endpoint);
    return {
      endpoint: subscription.endpoint,
      subscribed: device.subscribed,
      subscribers: device.subscribers,
    };
  } catch (error) {
    // Браузърът има абонамент, но BE не познава устройството (изтрито при
    // умрял endpoint) — третираме като „никой не е абониран".
    if (isAxiosError(error) && error.response?.status === 404) {
      return { endpoint: subscription.endpoint, subscribed: false, subscribers: [] };
    }
    throw error;
  }
}

// Дали функцията е налична изобщо: BE връща publicKey: null при изключен push
// (липсващи VAPID ключове) → целият UI се крие. Отговорът се кешира завинаги.
export function usePushFeatureEnabled(): boolean {
  const { data } = useQuery({
    queryKey: VAPID_KEY_QUERY_KEY,
    queryFn: getVapidPublicKey,
    staleTime: Infinity,
    retry: false,
  });
  return data?.publicKey != null;
}

export interface PushSubscriptionState {
  supported: boolean;
  featureEnabled: boolean;
  permission: NotificationPermission | null;
  /** Абониран ли е ТЕКУЩИЯТ потребител на това устройство. */
  subscribed: boolean;
  /** Всички абонирани на устройството (за екрана на общия акаунт). */
  subscribers: PushSubscriber[];
  /** Има ли браузърът изобщо push абонамент за това устройство. */
  hasDeviceSubscription: boolean;
  loading: boolean;
  busy: boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  removeSubscriber: (userId: number) => Promise<void>;
}

// active = отворен ли е модалът, който ползва hook-а — състоянието на
// устройството се чете само тогава.
export function usePushSubscription(active: boolean): PushSubscriptionState {
  const queryClient = useQueryClient();
  const supported = isPushSupported();
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(
    supported ? Notification.permission : null,
  );

  const vapidQuery = useQuery({
    queryKey: VAPID_KEY_QUERY_KEY,
    queryFn: getVapidPublicKey,
    staleTime: Infinity,
    retry: false,
  });
  const publicKey = vapidQuery.data?.publicKey ?? null;
  const featureEnabled = publicKey != null;

  const deviceQueryEnabled = active && supported && featureEnabled;
  const deviceQuery = useQuery({
    queryKey: DEVICE_QUERY_KEY,
    queryFn: readCurrentDevice,
    enabled: deviceQueryEnabled,
  });

  const enable = useCallback(async () => {
    if (!supported || !publicKey || busy) return;
    setBusy(true);
    try {
      // Редът е фиксиран: register → разрешение → subscribe. Разрешението се
      // иска САМО тук, във веригата на клика (Safari изисква жест).
      // Регистрацията е общата на приложението (lib/serviceWorker.ts) — един
      // worker носи и офлайн кеша, и push handler-ите.
      const registered = await registerAppServiceWorker();
      if (!registered) throw new Error("Service worker unavailable");
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      if (permissionResult !== "granted") return;
      // ready гарантира активен service worker — subscribe върху неактивна
      // регистрация хвърля InvalidStateError.
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      const keys = subscription.toJSON().keys;
      if (!keys?.p256dh || !keys?.auth) {
        throw new Error("Push subscription without keys");
      }
      await registerDevice({
        endpoint: subscription.endpoint,
        p256dh: keys.p256dh,
        auth: keys.auth,
      });
      await subscribeSelf(subscription.endpoint);
      await queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEY });
    } finally {
      setBusy(false);
    }
  }, [supported, publicKey, busy, queryClient]);

  const disable = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (!subscription) return;
      // Браузърният абонамент НЕ се разваля — маха се само връзката на
      // потребителя; останалите абонирани на устройството продължават.
      await unsubscribe(subscription.endpoint);
      await queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEY });
    } finally {
      setBusy(false);
    }
  }, [busy, queryClient]);

  const removeSubscriber = useCallback(
    async (userId: number) => {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (!subscription) return;
      await unsubscribe(subscription.endpoint, userId);
      await queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEY });
    },
    [queryClient],
  );

  return {
    supported,
    featureEnabled,
    permission,
    subscribed: deviceQuery.data?.subscribed ?? false,
    subscribers: deviceQuery.data?.subscribers ?? [],
    hasDeviceSubscription: (deviceQuery.data?.endpoint ?? null) !== null,
    loading:
      vapidQuery.isLoading || (deviceQueryEnabled && deviceQuery.isLoading),
    busy,
    enable,
    disable,
    removeSubscriber,
  };
}
